import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Square } from "lucide-react";

export const VOICE_FALLBACK_MSG = "Voice typing is not available in this browser right now. You can still type your answer.";

export function voiceErrorMessage(errCode) {
  if (errCode === "not-allowed" || errCode === "service-not-allowed") {
    return "Microphone access is blocked. You can allow the microphone in your browser settings, or just type your answer.";
  }
  if (errCode === "no-speech") {
    return "We didn't catch that. You can try again or type your answer.";
  }
  if (errCode === "audio-capture") {
    return "We couldn't find a microphone. You can still type your answer.";
  }
  return VOICE_FALLBACK_MSG;
}

/**
 * Voice-enabled textarea using the browser Web Speech API.
 * - No audio is uploaded to the server: only the transcript is saved.
 * - Hides the mic and shows a typed-fallback message when speech is unsupported.
 * - Dictation appends to the CURRENT text (never erases typed text).
 * - Applicant can review, edit, or delete the transcript before saving.
 */
export default function VoiceTextarea({
  value,
  onChange,
  placeholder,
  maxChars = 2500,
  testId,
  micTestId,
  disabled,
  rows = 5,
}) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(true);
  const [interim, setInterim] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const recogRef = useRef(null);
  // Always-fresh refs so recognition callbacks never work from stale text.
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  valueRef.current = value;
  onChangeRef.current = onChange;

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); return; }
    const r = new SR();
    r.continuous = true;
    r.interimResults = true;
    r.lang = "en-US";
    r.onresult = (ev) => {
      let finalText = "";
      let interimText = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const res = ev.results[i];
        if (res.isFinal) finalText += res[0].transcript;
        else interimText += res[0].transcript;
      }
      if (finalText) {
        const prev = valueRef.current || "";
        const sep = prev && !prev.endsWith(" ") ? " " : "";
        onChangeRef.current((prev + sep + finalText.trim()).slice(0, maxChars));
      }
      setInterim(interimText);
    };
    r.onend = () => { setListening(false); setInterim(""); };
    r.onerror = (ev) => {
      setListening(false);
      setInterim("");
      // Never touch the typed text on errors — just explain what happened.
      setErrMsg(voiceErrorMessage(ev?.error));
    };
    recogRef.current = r;
    return () => { try { r.stop(); } catch (e) { /* noop */ } };
  }, [maxChars]);

  const start = () => {
    if (!recogRef.current || disabled) return;
    setErrMsg("");
    try {
      recogRef.current.start();
      setListening(true);
    } catch (e) { /* already running */ }
  };
  const stop = () => {
    if (!recogRef.current) return;
    try { recogRef.current.stop(); } catch (e) { /* noop */ }
    setListening(false);
  };

  const count = (value || "").length;

  return (
    <div className="space-y-2">
      <div className="relative">
        <textarea
          data-testid={testId}
          rows={rows}
          className="p2p-input font-sans"
          style={{ minHeight: rows * 24 + 24 }}
          placeholder={placeholder}
          value={(value || "") + (interim ? (value && !value.endsWith(" ") ? " " : "") + interim : "")}
          onChange={(e) => { setErrMsg(""); onChange(e.target.value.slice(0, maxChars)); }}
          maxLength={maxChars}
          disabled={disabled}
        />
        {supported && (
          <button
            type="button"
            data-testid={micTestId}
            onClick={listening ? stop : start}
            disabled={disabled}
            className="absolute right-3 bottom-3 inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold transition"
            style={{
              background: listening ? "#B91C1C" : "#0B2D59",
              color: "#F4F8FB",
            }}
            aria-label={listening ? "Stop recording" : "Start voice input"}
          >
            {listening ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            {listening ? "Stop" : "Speak"}
          </button>
        )}
      </div>
      <div className="flex items-start justify-between gap-3 text-xs" style={{ color: "#475569" }}>
        <span>
          {!supported && (
            <span className="inline-flex items-center gap-1" data-testid={testId ? `${testId}-voice-fallback` : undefined}>
              <MicOff className="w-3 h-3 shrink-0" /> {VOICE_FALLBACK_MSG}
            </span>
          )}
          {supported && errMsg && (
            <span className="inline-flex items-center gap-1" style={{ color: "#8A6D1D" }}>
              <MicOff className="w-3 h-3 shrink-0" /> {errMsg}
            </span>
          )}
          {supported && !errMsg && listening && <span>Listening… you can pause anytime. Review and edit the text before continuing.</span>}
        </span>
        <span className="shrink-0">{count}/{maxChars}</span>
      </div>
    </div>
  );
}
